import LeftArrow from '@/assets/svg-icons/LeftArrow';
import RightArrow from '@/assets/svg-icons/RightArrow';
import IconButton from '@/components/common/IconButton';
import usePopUpQuery from '@/hooks/RQHooks/layout/usePopUpQuery';
import { GA_EVENT } from '@/lib/gtag';
import { useViewportStore } from '@/store/useViewportStore';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const EventPopUpCarousel = () => {
  const { data } = usePopUpQuery();

  const isMobile = useViewportStore((store) => store.isMobile);
  const [isVisible, setIsVisible] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  ]);

  const [curSlideIndex, setCurSlideIndex] = useState(0);

  const debouncedSetIndex = debounce((index) => {
    setCurSlideIndex(index);
  }, 100); // 100ms 딜레이

  const handleImageClick = () => {
    const currentPopUp = data?.[curSlideIndex];
    if (!currentPopUp?.linkUrl) return;

    GA_EVENT('select_promotion', {
      promotion_id: String(currentPopUp.id),
      promotion_name: currentPopUp.title || `event_popup_${curSlideIndex + 1}`,
      location_id: `EVENT_POPUP_${curSlideIndex + 1}`,
    });

    window.location.href = currentPopUp.linkUrl;
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleDontShowAgain = () => {
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

    localStorage.setItem('eventPopupHidden', oneWeekFromNow.getTime().toString()); // 다시 보지 않기 설정
    setIsVisible(false);
  };

  const handleLeftClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    emblaApi?.scrollPrev();
  };

  const handleRightClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    emblaApi?.scrollNext();
  };

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', () => {
        debouncedSetIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi, debouncedSetIndex]);

  useEffect(() => {
    const hiddenUntil = localStorage.getItem('eventPopupHidden');

    if (hiddenUntil) {
      const hiddenDate = new Date(parseInt(hiddenUntil));
      const now = new Date();

      if (now < hiddenDate) {
        setIsVisible(false);
        return;
      } else {
        // 기간이 지났으면 localStorage에서 제거
        localStorage.removeItem('eventPopupHidden');
      }
    }

    setIsVisible(true);
  }, []);

  if (!data || data.length === 0 || !isVisible) {
    return null;
  }

  return (
    <PopUpContainer>
      <PopUpBackground />
      <Content>
        {!isMobile && (
          <IconButton onClick={handleLeftClick}>
            <LeftArrow color="white" />
          </IconButton>
        )}
        <div>
          <EmblaCarouselOutWrapper onClick={handleImageClick} ref={emblaRef}>
            <EmblaCarouselContainer isMobile={isMobile}>
              {data?.map((item, index) => (
                <EmblaSlide key={index}>
                  <ImageContainer key={index}>
                    <PopupImage src={item.imageUrl} alt={item.title} />
                  </ImageContainer>
                </EmblaSlide>
              ))}
            </EmblaCarouselContainer>
            <CarouselIndexViewer>
              {curSlideIndex + 1}/{data?.length}
            </CarouselIndexViewer>
          </EmblaCarouselOutWrapper>
          <ButtonWrapper>
            <Button onClick={handleDontShowAgain}>7일간 보지 않기</Button>
            <Button onClick={handleClose}>닫기</Button>
          </ButtonWrapper>
        </div>
        {!isMobile && (
          <IconButton onClick={handleRightClick}>
            <RightArrow color="white" />
          </IconButton>
        )}
      </Content>
    </PopUpContainer>
  );
};

export default EventPopUpCarousel;

const PopUpContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
`;

const PopUpBackground = styled.div`
  background-color: #000000;
  opacity: 0.6;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
`;

const EmblaCarouselOutWrapper = styled.div`
  overflow: hidden;
  border-radius: 12px;
  position: relative;
`;
const EmblaCarouselContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  width: ${({ isMobile }) => (isMobile ? '312px' : '420px')};
  height: ${({ isMobile }) => (isMobile ? '324px' : '468px')};
`;
const EmblaSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
`;

const ImageContainer = styled.div`
  position: relative;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.gray200};
`;

const PopupImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const CarouselIndexViewer = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 50px;
  height: 24px;
  border-radius: 24px;
  background-color: #000000;
  color: white;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.2;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
`;

const Button = styled.button`
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.1s ease-in-out;
  &:active {
    transform: scale(0.95);
  }
`;
